// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { useCoinMetadata } from '@mysten/core';
import { SAFE_TYPE_ARG } from '@safecoin/safe/utils';
import clsx from 'clsx';
import { useState } from 'react';

export interface SafeCoinLogoProps {
	/** Sizing / rounding classes, applied to the image and to the fallback alike. */
	className?: string;
	/** Coin type to resolve metadata for. Defaults to the native coin. */
	coinType?: string;
}

/**
 * The coin's logo, taken from its on-chain CoinMetadata `iconUrl`.
 *
 * There is no glyph fallback: the icon package's `Safe` glyph carried the wrong
 * brand's artwork and has been removed, so when metadata carries no icon (or the
 * image fails to load) we show the symbol initials instead.
 */
export function SafeCoinLogo({ className, coinType = SAFE_TYPE_ARG }: SafeCoinLogoProps) {
	const { data: coinMeta } = useCoinMetadata(coinType);
	const [failed, setFailed] = useState(false);
	const label = coinMeta?.symbol || 'SAFE';

	if (coinMeta?.iconUrl && !failed) {
		return (
			<img
				src={coinMeta.iconUrl}
				alt={label}
				className={clsx('object-cover', className)}
				onError={() => setFailed(true)}
			/>
		);
	}

	return (
		<div
			role="img"
			aria-label={label}
			className={clsx(
				'flex items-center justify-center bg-gradient-to-r from-gradient-blue-start to-gradient-blue-end text-subtitleSmallExtra font-medium uppercase text-white',
				className,
			)}
		>
			{label.slice(0, 2)}
		</div>
	);
}
